import type { TFunction } from "i18next";

import type { Slice } from "../hooks/useDistributionSlices";

import { cn } from "@/lib/utils/cn";

interface Props {
  rows: Slice[];
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
  canOpenIntegration: boolean;
  onOpen: (name: string) => void;
  numberFormatter: Intl.NumberFormat;
  t: TFunction;
}

export function DistributionLegendList({
  rows,
  hoveredKey,
  onHover,
  canOpenIntegration,
  onOpen,
  numberFormatter,
  t,
}: Props) {
  return (
    <ul className="grid w-full min-w-0 grid-cols-3 overflow-y-auto">
      {rows.map((row) => {
        const dimmed = hoveredKey !== null && hoveredKey !== row.key;
        const clickable = canOpenIntegration && !row.isOther;
        const open = () => onOpen(row.key);
        return (
          <li
            key={row.key}
            onMouseEnter={() => !row.isOther && onHover(row.key)}
            onMouseLeave={() => onHover(null)}
            onClick={clickable ? open : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open();
                  }
                }
                : undefined
            }
            role={clickable ? "link" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={
              clickable
                ? t("home.activeCampaignDistribution.openIntegrationAria", {
                  name: row.label,
                })
                : undefined
            }
            className={cn(
              "flex items-center gap-3 rounded-lg px-2 py-1.5 transition-[opacity,background-color] duration-200 ease-out",
              dimmed && "opacity-40",
              !row.isOther && "hover:bg-muted/40",
              clickable
                ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                : !row.isOther && "cursor-default",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "size-2.5 shrink-0 rounded-full",
                row.isOther && "bg-neutral",
              )}
              style={row.isOther ? undefined : { backgroundColor: row.fill }}
            />
            <span
              className={cn(
                "flex-1 truncate text-sm",
                row.isOther && "text-muted-foreground",
              )}
            >
              {row.label}
            </span>
            <span className="text-sm tabular-nums text-muted-foreground">
              {numberFormatter.format(row.value)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
